import React, { useState, useEffect } from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { taxonomyString, getReadingMinutes, getRichTextWordCount } from 'utils/text';
import { SectionLayout } from 'components/Sections/SectionLayout';
import CallToActionCards from 'components/Sections/CallToActionCards';
import SummarySection from 'components/Sections/SummarySection';
import { WebsiteImage } from 'components/TitleDescription';
import ToolboxLayout from 'layouts/toolboxLayout';
import TableOfContents from 'components/Page/BlogDetailsTemplate/BlogTableOfContents';
import useIsClient from 'hooks/useIsClient';
import StyledRichText from 'components/RichText/StyledRichText';
import Text from 'components/Core/Text';
import MarketoForm from 'components/Form/MarketoForm';
import AddThisSocialSharing from 'components/AddThisSocialSharing';
import useBlogPosts from 'hooks/useBlogPosts';
import useCoronaPosts from 'hooks/useCoronaPosts';
import parseRichTextArticle from 'utils/parseRichTextArticle';
import getBreakpointView from 'utils/getBreakpointView';
import GlassesIcon from 'components/Brand/Icons/static/glasses.svg';
import ClockIcon from 'components/Brand/Icons/static/clock.svg';
import UserIcon from 'components/Brand/Icons/static/user.svg';
import SidebarV2 from 'components/Sections/Sidebar/SidebarV2';
import TocRichText from 'components/RichText/ContentfulRichText/TableOfContents/TocRichText';
import BookmarkContext from 'utils/BookmarkContext';

const coronavirus = 'coronavirus';

const ArticleDescriptors = ({ iconData, text }) =>
  text ? (
    <Col>
      <span>
        <img src={iconData.src} alt={iconData.alt} />
        {text}
      </span>
    </Col>
  ) : null;

const BlogDetailsTemplate = props => {
  const {
    id,
    title,
    featuredImage,
    inlineMarketingForm,
    publishDate,
    authorReference,
    contentRichText,
    summary,
    taxonomy,
    seo: seoContent,
    tableOfContents: useSideBarToc,
  } = props.data.contentfulBlog;
  const [toc, setToc] = useState(null);
  const [body, setBody] = useState(null);

  const isClient = useIsClient();
  const hubType = props.pageContext.hub;
  const relatedPosts = hubType === coronavirus ? useCoronaPosts(id) : useBlogPosts(id);
  const isMobile = getBreakpointView(['xs', 'sm', 'md']);
  const readingMinutes = getReadingMinutes(getRichTextWordCount(contentRichText));

  const articleDescriptorData = [
    {
      iconData: { src: UserIcon, alt: 'User Icon' },
      text:
        authorReference?.firstName &&
        [authorReference?.firstName, authorReference?.lastName].join(' '),
    },
    { iconData: { src: ClockIcon, alt: 'Clock Icon' }, text: publishDate },
    { iconData: { src: GlassesIcon, alt: 'Glasses Icon' }, text: `${readingMinutes} Min Read` },
  ];

  const breadcrumbs = {
    data: [
      {
        title: 'Toolbox',
        icon: null,
        url: '/toolbox',
      },
      {
        title: hubType === coronavirus ? 'Coronavirus' : 'Blog',
        icon: null,
        url: hubType === coronavirus ? '/coronavirus' : '/blog',
      },
    ],
    absolute: true,
    theme: 'dark',
  };

  const bookmarkData = {
    subTitle: taxonomyString(taxonomy),
    image: featuredImage?.file?.url,
    type: 'Article',
    title,
    url: props.path,
  };

  useEffect(() => {
    const parsedBlog = parseRichTextArticle(contentRichText, useSideBarToc ? 'v2' : 'v1');
    setToc(parsedBlog.toc);
    setBody(parsedBlog.body);
  }, []);

  return (
    <BookmarkContext.Provider value={bookmarkData}>
      <ToolboxLayout
        breadcrumbs={breadcrumbs}
        contentfulSeo={{
          ...seoContent,
          pageCategory: hubType === coronavirus ? 'Corona Virus' : 'Blog',
          featuredImage: seoContent?.featuredImage || { image: featuredImage },
        }}
        useProgressbar
      >
        <SectionLayout backgroundColor="white">
          <BlogDetails>
            <Col>
              {taxonomy && (
                <Row className="section-blog-details-category" justify="center">
                  <Text type="overline" color="Blue 5">
                    {taxonomyString(taxonomy)}
                  </Text>
                </Row>
              )}

              <Row className="section-blog-details-titles" justify="center">
                <BlogDetailsTitle>{title}</BlogDetailsTitle>
              </Row>
              <BlogAuthorDate>
                <Row justify="center" gutter={[40, 0]}>
                  {articleDescriptorData.map((item, index) => (
                    <ArticleDescriptors {...item} key={index} />
                  ))}
                </Row>
              </BlogAuthorDate>
              <AddThisSocialSharing placement="mobile" />
            </Col>
            <Row className="section-blog-details-contents" type="flex" justify="center">
              <Col className="section-blog-details--main-contents" sm={24} md={24} lg={16} xl={16}>
                {featuredImage && isClient && (
                  <WebsiteImage src={featuredImage} alt={title} width="100%" marginBottom="32px" />
                )}

                <Col
                  md={24}
                  lg={22}
                  xl={22}
                  style={inlineMarketingForm ? { paddingTop: '40px' } : ''}
                >
                  {inlineMarketingForm?.marketoForm && (
                    <MarketoForm
                      inlineMarketingForm={inlineMarketingForm}
                      backgroundColor="#009de6"
                      titleColor="#ffffff"
                      theme="blog"
                      className="blog-details--watch-full-video-form"
                      shadow
                      showPrivacy={false}
                      formType="Blog Details Form"
                      fontSize="16px"
                      notificationStyles="section-blog-details-main-form-notification"
                      notificationMessage="Thanks for subscribing!"
                      revealProps={{
                        styles: 'section-blog-details-main-form-legal-info',
                        content: (
                          <>
                            By entering your information above and clicking this button, you agree
                            that we may contact you at the phone number or email address you provide
                            in this form. Our
                            <a href="/legal/terms-of-use" target="_blank">
                              Terms of Use
                            </a>
                            and
                            <a href="/legal/privacy-policy" target="_blank">
                              Privacy Policy
                            </a>
                            apply.
                          </>
                        ),
                      }}
                    />
                  )}
                  {toc && !useSideBarToc && <TableOfContents content={toc} />}
                  {contentRichText &&
                    (useSideBarToc ? (
                      <TocRichText content={body} toc={toc} />
                    ) : (
                      <StyledRichText content={!toc ? contentRichText?.json : body} />
                    ))}
                  {summary && (
                    <SummarySection
                      className="test-summary-section"
                      headline={summary.headline}
                      subHead={summary.subhead}
                      colorScheme={summary.colorScheme}
                      callToAction={summary.cta}
                      backgroundImage={summary.backgroundImage}
                    />
                  )}
                </Col>
              </Col>

              <Col xs={24} sm={24} lg={8} xl={8} className="section-blog-details-sidebar">
                <AddThisSocialSharing placement="blog" />
                <SidebarV2 toc={useSideBarToc && !isMobile && toc} demo email />
              </Col>
            </Row>
          </BlogDetails>
        </SectionLayout>
        <CallToActionCards
          title="Related post"
          backgroundColor="White"
          cardType="Image"
          bodyType="blog"
          cardInfo={relatedPosts}
          hubType={hubType}
          position="related posts cta"
          cta={{ buttonLabel: 'See more posts', color: 'Orange', url: `/${hubType}` }}
        />
      </ToolboxLayout>
    </BookmarkContext.Provider>
  );
};

export default BlogDetailsTemplate;

export const BlogDetailsTemplateQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    contentfulBlog(seo: { pageUrl: { eq: $slug } }) {
      id
      title
      seo {
        ...Seo
      }
      publishDate(formatString: "MMMM Do, YYYY")
      authorReference {
        ...Person
      }
      featuredImage {
        title
        description
        file {
          url
        }
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      inlineMarketingForm {
        ...InlineMarketingForm
      }
      summary {
        ...ConversionPanel
      }
      contentRichText {
        json
      }
      taxonomy {
        ...Taxonomy
      }
      sideBar {
        ...SideBar
      }
      tableOfContents
    }
  }
`;

const BlogDetails = styled.div`
  font-family: Sofia Pro;
  padding: 60px 0 0 0;

  .signup-form--title {
    text-align: center;
  }
  .signup-form--form {
    padding: 33px 26px;
    background-color: white;
  }
  .section-blog-details-category {
    margin-bottom: 10px;
    h3 {
      font-size: 16px;
      font-weight: 600;
      line-height: 110%;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      color: var(--blue-6);
      text-align: center;
    }
    @media (max-width: 992px) {
      justify-content: start;
    }
  }

  .section-blog-details-main-form-legal-info {
    opacity: 1;
    width: 100%;
    font-size: 13px;
    font-weight: 500;
    line-height: 150%;
    text-align: center;
    p {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  .section-blog-details-main-form-notification {
    text-align: center;
  }

  .section-blog-details-contents {
    margin: 0px auto !important;

    .section-blog-details-return-to-blog {
      a h4 {
        font-size: 20px;
        font-weight: bold;
        line-height: 110%;
        letter-spacing: -0.01em;
        color: var(--secondary-color);
      }
    }
    .section-blog-details--sidebar-contents {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }
    .section-blog-details-sidebar {
      margin-left: -20px;
      margin-top: 60px;
      @media (max-width: 992px) {
        margin-left: 0px;
        margin-top: 50px;
      }
    }
    .section-blog-details-form-notification {
      max-width: 215px;
      margin: 0 auto 20px auto;
      text-align: center;
    }
  }

  @media (max-width: 1200px) {
    .blog-details--watch-full-video-form {
      width: 100%;
    }
  }

  @media (max-width: 992px) {
    width: 100%;
    .section-blog-details-contents {
      flex-direction: row;
      .section-blog-details--main-contents {
        width: 100%;
      }

      .section-blog-details--sidebar-contents {
        width: 100%;
        padding-top: 30px;
        align-items: start;
      }
    }
  }
`;

const BlogDetailsTitle = styled.h1`
  font-size: 49px;
  font-weight: 600;
  line-height: 110%;
  letter-spacing: -0.04em;
  color: var(--neutral-7);
  margin-bottom: 20px;
  max-width: 984px;
  text-align: center;
  @media (max-width: 992px) {
    text-align: left;
  }
  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 576px) {
    font-size: 28px;
  }
`;

const BlogAuthorDate = styled.div`
  width: 100%;
  margin: 0px auto 45px;
  img {
    margin-right: 8px;
    margin-bottom: 3px;
  }
  span {
    text-transform: uppercase;
    font-size: 13px;
    line-height: 150%;
    letter-spacing: 0.05em;
    color: var(--neutral-text-color);
  }
  @media (max-width: 992px) {
    margin-bottom: 0;
    .ant-row {
      justify-content: start;
    }
  }
`;
